import { Toaster, TooltipProvider } from "@veritrellis/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AnalyticsProvider } from "./components/AnalyticsProvider.js";
import { AdminLayout } from "./layout/AdminLayout.js";
import { createQueryClient } from "./lib/queryClient.js";
import { ApprovalsPage } from "./routes/ApprovalsPage.js";
import { AuditPage } from "./routes/AuditPage.js";
import { DashboardPage } from "./routes/DashboardPage.js";
import { OnboardingPage } from "./routes/OnboardingPage.js";
import { PoliciesListPage } from "./routes/PoliciesListPage.js";
import { PolicyEditPage } from "./routes/PolicyEditPage.js";
import { RequestDetailPage } from "./routes/RequestDetailPage.js";
import { RequestsListPage } from "./routes/RequestsListPage.js";
import { RequireAuth } from "./routes/RequireAuth.js";
import { ApproverGroupsPage } from "./routes/ApproverGroupsPage.js";
import { ConnectorsPage } from "./routes/ConnectorsPage.js";
import { NewPolicyPage } from "./routes/NewPolicyPage.js";
import { PolicySimulatePage } from "./routes/PolicySimulatePage.js";
import { SettingsLayout } from "./layout/SettingsLayout.js";
import { SettingsAccessPage } from "./routes/SettingsAccessPage.js";
import { SettingsApiKeysPage } from "./routes/SettingsApiKeysPage.js";
import { SettingsMembersPage } from "./routes/SettingsMembersPage.js";
import { SettingsAccountPage } from "./routes/SettingsAccountPage.js";
import { SettingsNotificationsPage } from "./routes/SettingsNotificationsPage.js";
import { SettingsWorkspacePage } from "./routes/SettingsWorkspacePage.js";
import { SettingsBillingPage } from "./routes/SettingsBillingPage.js";
import { CheckoutRedirectPage } from "./routes/CheckoutRedirectPage.js";
import { SignInPage } from "./routes/SignInPage.js";

const queryClient = createQueryClient();

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <AnalyticsProvider>
            <Routes>
              <Route path="/sign-in" element={<SignInPage />} />
              <Route element={<RequireAuth />}>
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/checkout" element={<CheckoutRedirectPage />} />
                <Route element={<AdminLayout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/requests" element={<RequestsListPage />} />
                  <Route path="/requests/:id" element={<RequestDetailPage />} />
                  <Route path="/approvals" element={<ApprovalsPage />} />
                  <Route path="/policies" element={<PoliciesListPage />} />
                  <Route path="/policies/new" element={<NewPolicyPage />} />
                  <Route path="/policies/:actionType" element={<PolicyEditPage />} />
                  <Route path="/policies/:actionType/simulate" element={<PolicySimulatePage />} />
                  <Route path="/audit" element={<AuditPage />} />
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route index element={<SettingsAccountPage />} />
                    <Route path="account" element={<SettingsAccountPage />} />
                    <Route path="notifications" element={<SettingsNotificationsPage />} />
                    <Route path="workspace" element={<SettingsWorkspacePage />} />
                    <Route path="access" element={<SettingsAccessPage />} />
                    <Route path="approver-groups" element={<ApproverGroupsPage />} />
                    <Route path="connectors" element={<ConnectorsPage />} />
                    <Route path="api-keys" element={<SettingsApiKeysPage />} />
                    <Route path="members" element={<SettingsMembersPage />} />
                    <Route path="billing" element={<SettingsBillingPage />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AnalyticsProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};
